import { RelatedPartsService } from '../../services/related-parts-service';
import removeNamespace from '../removeNamespace';
import { RelatedPartsInfo } from '@/entities/RelatedPartsInfo';
import { RelatedPart } from '@/entities/RelatedPart';
import { MutationTree, ActionTree, ActionContext, GetterTree, Module } from 'vuex';
import { LoadingState } from '@/entities/LoadingState';
import { RootState } from '../rootState';

const relatedPartsService = new RelatedPartsService();

const namespace = 'relatedParts';

export const RelatedPartsModuleTypes = {
    getters: {
        partId: `${namespace}/partId`,
        alternativeParts: `${namespace}/alternativeParts`,
        replacementParts: `${namespace}/replacementParts`,
        childBrandParts: `${namespace}/childBrandParts`,
        relatedParts: `${namespace}/relatedParts`,
        relatedPartsLoadingState: `${namespace}/relatedPartsLoadingState`,
    },
    mutations: {
        loadRelatedParts: `${namespace}/loadRelatedParts`,
        unloadRelatedParts: `${namespace}/unloadRelatedParts`,
        loadRelatedPartsSuccess: `${namespace}/loadRelatedPartsSuccess`,
        loadRelatedPartsFail: `${namespace}/loadRelatedPartsFail`,
    },
    actions: {
        loadRelatedParts: `${namespace}/loadRelatedParts`,
        unloadRelatedParts: `${namespace}/unloadRelatedParts`,
    },
};

const types = removeNamespace(namespace, RelatedPartsModuleTypes);

export interface RelatedPartsState {
    relatedPartsLoadingState: LoadingState | null;
    relatedParts: { [id: string]: RelatedPartsInfo; };
    partId: string | null;
}

const state: RelatedPartsState = {
    relatedPartsLoadingState: LoadingState.Indeterminate,
    relatedParts: {},
    partId: null,
};

const mutations: MutationTree<RelatedPartsState> = {
    [types.mutations.loadRelatedParts](currentState: RelatedPartsState, payload: string): void {
        currentState.relatedPartsLoadingState = LoadingState.Loading;
        currentState.partId = payload;
    },
    [types.mutations.loadRelatedPartsSuccess](currentState: RelatedPartsState, payload: RelatedPartsInfo): void {
        currentState.relatedPartsLoadingState = LoadingState.Loaded;
        currentState.relatedParts = {
            ...currentState.relatedParts,
            [payload.partId]: payload,
        };
    },
    [types.mutations.loadRelatedPartsFail](currentState: RelatedPartsState): void {
        currentState.relatedPartsLoadingState = LoadingState.Failed;
    },
    [types.mutations.unloadRelatedParts](currentState: RelatedPartsState, payload: string): void {
        currentState.relatedPartsLoadingState = LoadingState.Indeterminate;
        currentState.partId = null;
    },
};

const actions: ActionTree<RelatedPartsState, RootState> = {
    [types.actions.loadRelatedParts](context: ActionContext<RelatedPartsState, RootState>, payload: { id: string }): void {
        context.commit(types.mutations.loadRelatedParts, payload.id);
        if (!context.state.relatedParts[payload.id]) {
            relatedPartsService.getRelatedPartInfo(payload.id)
                .then((response) => {
                    context.commit(types.mutations.loadRelatedPartsSuccess, response);
                })
                .catch((e) => {
                    context.commit(types.mutations.loadRelatedPartsFail);
                });
        } else {
            context.commit(types.mutations.loadRelatedPartsSuccess, context.state.relatedParts[payload.id]);
        }
    },
    [types.actions.unloadRelatedParts](context: ActionContext<RelatedPartsState, RootState>): void {
        context.commit(types.mutations.unloadRelatedParts);
    },
};

const getters: GetterTree<RelatedPartsState, RootState> = {
    [types.getters.partId]: (currentState) => {
        return currentState.partId;
    },
    [types.getters.alternativeParts]: (currentState) => {
        return currentState.partId && currentState.relatedParts[currentState.partId] && currentState.relatedParts[currentState.partId].alternativeParts;
    },
    [types.getters.replacementParts]: (currentState) => {
        return currentState.partId && currentState.relatedParts[currentState.partId] && currentState.relatedParts[currentState.partId].replacementParts;
    },
    [types.getters.childBrandParts]: (currentState) => {
        return currentState.partId && currentState.relatedParts[currentState.partId] && currentState.relatedParts[currentState.partId].childBrandParts;
    },
    [types.getters.relatedParts]: (currentState) => {
        return currentState.partId &&
            currentState.relatedParts[currentState.partId] &&
            [...currentState.relatedParts[currentState.partId].replacementParts, ...currentState.relatedParts[currentState.partId].alternativeParts];
    },
    [types.getters.relatedPartsLoadingState]: (currentState) => {
        return currentState.relatedPartsLoadingState;
    },
};

const module: Module<RelatedPartsState, RootState> = {
    state,
    mutations,
    actions,
    getters,
    namespaced: true,
};

export default module;
